import React, { useState, useEffect, useMemo } from 'react';
import '../ResultsTable.css'; // Ensure this CSS file is correctly linked and styled
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ClipLoader from 'react-spinners/ClipLoader'; // Assuming you've installed this
import {
  faEnvelope,
  faFileAlt,
  faStar,
  faPaperPlane,
  faCalendarAlt,
  faUserTie,
  faFilter,
  faSave,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faUserCheck,
  faUndo,
  faListAlt,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { backend_url } from '../config';
import { Link } from 'react-router-dom';

function ResultsTable({ analysisResults }) {
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [scoreThreshold, setScoreThreshold] = useState(55);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveIntent, setSaveIntent] = useState('default');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    console.log('ResultsTable received analysisResults:', analysisResults);
    if (analysisResults && analysisResults.length > 0) {
      console.log('First result structure:', analysisResults[0]);
    }
  }, [analysisResults]);

  const processedResults = useMemo(() => {
    if (!analysisResults || !Array.isArray(analysisResults)) {
      return [];
    }
    return analysisResults
      .map((item) => ({
        fileName: item.fileName || 'N/A',
        name: item.analysis?.name || 'N/A',
        email: item.analysis?.email || 'N/A',
        phone: item.analysis?.phone || 'N/A',
        location: item.analysis?.location || 'N/A',
        keySkills: item.analysis?.keySkills || [],
        totalExperienceYears: item.analysis?.totalExperienceYears || 0,
        mainDomain: item.analysis?.mainDomain || 'N/A',
        companiesWorked: item.analysis?.companiesWorked || [],
        education: item.analysis?.education || [],
        similarityScore:
          typeof item.analysis?.similarityScore === 'number'
            ? item.analysis.similarityScore
            : 0,
        commonSkills: item.analysis?.commonSkills || [],
        preferred: item.analysis?.preferred || 'No',
        roleName: item.analysis?.roleName || 'N/A',
        resumeSummary: item.summary || 'N/A',
        error: item.error,
        analysisError: item.analysis?.error,
      }))
      .sort((a, b) => b.similarityScore - a.similarityScore);
  }, [analysisResults]);

  const highlightedResults = useMemo(() => {
    return processedResults.filter(
      (result) =>
        result.similarityScore >= scoreThreshold &&
        !result.error &&
        !result.analysisError
    );
  }, [processedResults, scoreThreshold]);

  const displayResults = processedResults;

  const toggleCandidateSelection = (email) => {
    setSelectedCandidates((prevSelected) =>
      prevSelected.includes(email)
        ? prevSelected.filter((e) => e !== email)
        : [...prevSelected, email]
    );
    setSaveIntent('default');
  };

  const generateMailLink = (email, candidateData) => {
    if (!email || email === 'N/A') {
      alert('Cannot generate mail link: Candidate email is not available.');
      return;
    }
    const subject = encodeURIComponent(
      'Follow Up: Your Application with CoreOps.ai'
    );

    // --- GOOGLE FORM INTEGRATION (OPTIONAL) ---
    const googleFormBaseUrl =
      'https://docs.google.com/forms/d/e/YOUR_GOOGLE_FORM_ID_HERE/viewform'; // <<<< REPLACE IF USING

    let formLinkForBody = '';

    if (googleFormBaseUrl.includes('YOUR_GOOGLE_FORM_ID_HERE')) {
      console.warn(
        "Google Form ID is not configured in ResultsTable.js. Email will be sent without a pre-filled form link. Update 'YOUR_GOOGLE_FORM_ID_HERE' and entry IDs if needed."
      );
    } else {
      // IMPORTANT: Replace these with your actual Google Form pre-fill entry IDs
      formLinkForBody = `${googleFormBaseUrl}?usp=pp_url&entry.YOUR_NAME_FIELD_ID=${encodeURIComponent(
        candidateData.name || ''
      )}&entry.YOUR_EMAIL_FIELD_ID=${encodeURIComponent(
        candidateData.email || ''
      )}&entry.YOUR_SCORE_FIELD_ID=${encodeURIComponent(
        candidateData.similarityScore || 0
      )}`;
    }

    let emailBodyContent = `Dear ${
      candidateData.name || 'Candidate'
    },\n\nThank you for your interest in a position at CoreOps.ai. We were impressed with your resume.\n\n`;

    if (formLinkForBody) {
      emailBodyContent += `To help us learn a bit more about you, please take a few minutes to complete this short form: ${formLinkForBody}\n\n`;
    } else {
      emailBodyContent += `We would like to discuss your application further. Please let us know your availability for a brief call.\n\n`;
    }

    emailBodyContent += `We look forward to hearing from you.\n\nBest regards,\nTalent Acquisition Team\nCoreOps.ai`;

    const body = encodeURIComponent(emailBodyContent);
    const outlookLink = `https://outlook.office.com/mail/deeplink/compose?to=${email}&subject=${subject}&body=${body}`;

    console.log('Opening Outlook link:', outlookLink);
    window.open(outlookLink, '_blank');
  };

  const generateBulkMailLink = (candidatesToMail) => {
    if (candidatesToMail.length === 0) return;
    candidatesToMail.forEach((candidate) => {
      if (candidate.email && candidate.email !== 'N/A') {
        // Ensure email exists
        generateMailLink(candidate.email, candidate);
      } else {
        console.warn(
          `Skipping email for ${
            candidate.name || 'a candidate'
          } due to missing email.`
        );
      }
    });
  };

  const generateCalendarInvite = (candidateData) => {
    if (!candidateData.email || candidateData.email === 'N/A') {
      alert(
        'Cannot generate calendar invite: Candidate email is not available.'
      );
      return;
    }

    const subject = `Interview: ${
      candidateData.name || 'Candidate'
    } with CoreOps.ai`;
    // Basic body, can be enhanced with HTML
    const description = `Dear ${
      candidateData.name || 'Candidate'
    },\n\nThis is an invitation to discuss your application for a position at CoreOps.ai.\n\nPlease confirm your availability.\n\nWe look forward to speaking with you.\n\nBest regards,\nTalent Acquisition Team\nCoreOps.ai`;

    // Outlook Calendar Deeplink (basic example)
    // For specific times, you'd need to format start and end datetimes
    // Example: &startdt=2024-01-01T10:00:00&enddt=2024-01-01T11:00:00
    // This requires getting user input for date/time or having predefined slots.
    // For now, it opens a new event compose screen.
    const outlookCalendarLink =
      `https://outlook.office.com/calendar/0/deeplink/compose` +
      `?path=/calendar/action/compose` +
      `&rru=addevent` + // Ensure it's adding an event
      `&to=${encodeURIComponent(candidateData.email)}` +
      `&subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(description)}`;

    console.log('Opening Outlook Calendar link:', outlookCalendarLink);
    window.open(outlookCalendarLink, '_blank');
  };

  const selectAllQualifiedCandidates = () => {
    const qualifiedEmails = highlightedResults
      .filter((result) => !result.error && !result.analysisError)
      .map((result) => result.email);
    setSelectedCandidates(qualifiedEmails);
    if (qualifiedEmails.length > 0) {
      setSaveIntent('qualified');
    } else {
      setSaveIntent('default');
    }
  };

  const selectAllCandidates = () => {
    const allDisplayEmails = displayResults
      .filter((result) => !result.error && !result.analysisError)
      .map((result) => result.email);
    setSelectedCandidates(allDisplayEmails);
    setSaveIntent('default');
  };

  const clearAllSelections = () => {
    setSelectedCandidates([]);
    setSaveIntent('default');
  };

  // const saveSelectedCandidates = async () => {
  //   if (selectedCandidates.length === 0) {
  //       setSaveError("Please select at least one candidate to save.");
  //       setTimeout(() => setSaveError(null), 3000);
  //       return;
  //   }

  //   setIsSaving(true);
  //   setSaveSuccess(false);
  //   setSaveError(null);
  //   setSuccessMessage('');

  //   const candidatesToSave = processedResults
  //     .filter((candidate) => selectedCandidates.includes(candidate.email))
  //     .map((candidate) => ({
  //       name: candidate.name,
  //       email: candidate.email,
  //       phone: candidate.phone,
  //       location: candidate.location,
  //       keySkills: candidate.keySkills,
  //       totalExperienceYears: candidate.totalExperienceYears,
  //       mainDomain: candidate.mainDomain,
  //       resumeSummary: candidate.resumeSummary,
  //       similarityScore: candidate.similarityScore,
  //       preferred: candidate.preferred,
  //       roleName: candidate.roleName,
  //       fileName: candidate.fileName
  //     }));

  //   if (candidatesToSave.length === 0) {
  //       setIsSaving(false);
  //       return;
  //   }

  //   let endpoint = `${backend_url}/candidates`;
  //   let currentSaveType = 'default';

  //   if (saveIntent === 'qualified') {
  //     const allSelectedAreTrulyQualified = candidatesToSave.every(
  //       (c) => c.similarityScore >= scoreThreshold
  //     );
  //     if (allSelectedAreTrulyQualified && candidatesToSave.length > 0) {
  //       endpoint = `${backend_url}/qualified-candidates`;
  //       currentSaveType = 'qualified';
  //     } else if (candidatesToSave.length > 0) {
  //       console.warn("Save intent 'qualified', but selection mixed or empty after filtering. Saving to general.");
  //     }
  //   }

  //   try {
  //     console.log(`Saving ${candidatesToSave.length} candidates to ${endpoint}`);
  //     const response = await fetch(endpoint, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ candidates: candidatesToSave }),
  //     });
  //     const responseData = await response.json();
  //     if (!response.ok) {
  //       throw new Error(responseData.message || `Server error: ${response.status}`);
  //     }
  //     setSaveSuccess(true);
  //     setSuccessMessage(
  //       responseData.message || `${candidatesToSave.length} ${
  //         currentSaveType === 'qualified' ? 'qualified ' : ''
  //       }candidate(s) saved successfully!`
  //     );
  //     setTimeout(() => { setSaveSuccess(false); setSuccessMessage(''); }, 4000);
  //   } catch (error) {
  //     console.error('Error saving candidates:', error);
  //     setSaveError(error.message || `An error occurred while saving candidates.`);
  //     setTimeout(() => setSaveError(null), 5000);
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  // Replace the saveSelectedCandidates function in your ResultsTable.js with this fixed version:

  const saveSelectedCandidates = async () => {
    if (selectedCandidates.length === 0) {
      setSaveError('Please select at least one candidate to save.');
      setTimeout(() => setSaveError(null), 3000);
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    setSuccessMessage('');

    const candidatesToSave = processedResults
      .filter((candidate) => selectedCandidates.includes(candidate.email))
      .map((candidate) => {
        // FIXED: Include ALL the extracted data
        const candidateData = {
          name: candidate.name,
          email: candidate.email,
          phone: candidate.phone,
          location: candidate.location,
          keySkills: candidate.keySkills,
          totalExperienceYears: candidate.totalExperienceYears,
          mainDomain: candidate.mainDomain,

          // ✅ FIXED: Include the missing data!
          companiesWorked: candidate.companiesWorked,
          education: candidate.education,
          commonSkills: candidate.commonSkills,

          resumeSummary: candidate.resumeSummary,
          similarityScore: candidate.similarityScore,
          preferred: candidate.preferred,
          roleName: candidate.roleName,
          fileName: candidate.fileName,
        };

        // DEBUG: Log what we're about to save
        console.log(
          '🔍 DEBUG - Candidate being prepared for save:',
          candidateData.name
        );
        console.log(
          '🔍 DEBUG - Companies to save:',
          candidateData.companiesWorked
        );
        console.log('🔍 DEBUG - Education to save:', candidateData.education);

        return candidateData;
      });

    if (candidatesToSave.length === 0) {
      setIsSaving(false);
      return;
    }

    let endpoint = `${backend_url}/candidates`;
    let currentSaveType = 'default';

    if (saveIntent === 'qualified') {
      const allSelectedAreTrulyQualified = candidatesToSave.every(
        (c) => c.similarityScore >= scoreThreshold
      );
      if (allSelectedAreTrulyQualified && candidatesToSave.length > 0) {
        endpoint = `${backend_url}/qualified-candidates`;
        currentSaveType = 'qualified';
      } else if (candidatesToSave.length > 0) {
        console.warn(
          "Save intent 'qualified', but selection mixed or empty after filtering. Saving to general."
        );
      }
    }

    try {
      console.log(
        `Saving ${candidatesToSave.length} candidates to ${endpoint}`
      );

      // DEBUG: Log the full payload being sent
      console.log(
        '🔍 DEBUG - Full payload being sent:',
        JSON.stringify(candidatesToSave, null, 2)
      );

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidates: candidatesToSave }),
      });
      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(
          responseData.message || `Server error: ${response.status}`
        );
      }
      setSaveSuccess(true);
      setSuccessMessage(
        responseData.message ||
          `${candidatesToSave.length} ${
            currentSaveType === 'qualified' ? 'qualified ' : ''
          }candidate(s) saved successfully!`
      );
      setTimeout(() => {
        setSaveSuccess(false);
        setSuccessMessage('');
      }, 4000);
    } catch (error) {
      console.error('Error saving candidates:', error);
      setSaveError(
        error.message || `An error occurred while saving candidates.`
      );
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  if (!analysisResults) {
    return (
      <div className='coreops-page-container results-page'>
        <div className='coreops-content-wrapper'>
          <div className='coreops-message-container loading'>
            <FontAwesomeIcon icon={faSpinner} spin size='3x' />
            <p>Loading Candidate Analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (displayResults.length === 0) {
    return (
      <div className='coreops-page-container results-page'>
        <div className='coreops-content-wrapper'>
          <header className='coreops-main-header results-sub-header'>
            <h1>
              <FontAwesomeIcon icon={faListAlt} /> Candidate Analysis
            </h1>
          </header>
          <div className='coreops-message-container empty'>
            <FontAwesomeIcon icon={faFileAlt} size='4x' />
            <p className='empty-title'>No Candidate Data Available</p>
            <p className='empty-subtitle'>
              It seems no resumes were processed or they didn't produce any
              results.
            </p>
            <Link to='/' className='coreops-button coreops-button-primary'>
              <FontAwesomeIcon icon={faUndo} /> Go Back to Upload
            </Link>
          </div>
          <footer className='coreops-footer results-sub-footer'>
            <p>© {new Date().getFullYear()} CoreOps.ai</p>
          </footer>
        </div>
      </div>
    );
  }

  const candidatesAvailableForSelection = displayResults.filter(
    (r) => !r.error && !r.analysisError
  ).length;
  const highlightedAvailableForSelection = highlightedResults.length;

  return (
    <div className='coreops-page-container results-page'>
      <div className='coreops-content-wrapper'>
        <div className='coreops-results-table-container'>
          <header className='coreops-main-header results-sub-header'>
            <h1>
              <FontAwesomeIcon icon={faListAlt} /> Candidate Analysis Results
            </h1>
            <div className='coreops-results-summary-global'>
              Showing {displayResults.length} candidate profile(s).
              {scoreThreshold > 0 && highlightedResults.length > 0
                ? ` (${highlightedResults.length} with score ≥ ${scoreThreshold} are highlighted)`
                : scoreThreshold > 0
                ? ` (0 candidates meet score ≥ ${scoreThreshold})`
                : ''}
            </div>
          </header>

          <div className='coreops-controls-panel'>
            <div className='coreops-filter-section'>
              <button
                className={`coreops-button coreops-button-secondary filter-toggle-button ${
                  showFilterOptions ? 'active' : ''
                }`}
                onClick={() => setShowFilterOptions(!showFilterOptions)}
              >
                <FontAwesomeIcon icon={faFilter} /> Filters
              </button>
              {showFilterOptions && (
                <div className='coreops-filter-options-active'>
                  <label htmlFor='scoreFilter'>Min. Score:</label>
                  <input
                    type='range'
                    id='scoreFilter'
                    min='0'
                    max='100'
                    value={scoreThreshold}
                    onChange={(e) =>
                      setScoreThreshold(parseInt(e.target.value))
                    }
                  />
                  <span className='coreops-score-display'>
                    {scoreThreshold}
                  </span>
                  <button
                    className='coreops-button-inline reset-filter'
                    onClick={() => setScoreThreshold(55)}
                    title='Reset score to default (55)'
                  >
                    <FontAwesomeIcon icon={faUndo} />
                  </button>
                </div>
              )}
            </div>

            {displayResults.length > 0 && (
              <div className='coreops-bulk-actions-container'>
                <div className='coreops-selected-count'>
                  {selectedCandidates.length > 0
                    ? `${selectedCandidates.length} selected ${
                        saveIntent === 'qualified' &&
                        selectedCandidates.length > 0 &&
                        selectedCandidates.every((email) =>
                          highlightedResults.find(
                            (hr) =>
                              hr.email === email &&
                              !hr.error &&
                              !hr.analysisError
                          )
                        )
                          ? '(Target: Qualified DB)'
                          : '(Target: General DB)'
                      }`
                    : 'No candidates selected'}
                </div>
                <div className='coreops-action-buttons-group'>
                  <button
                    className='coreops-button coreops-button-secondary'
                    onClick={selectAllCandidates}
                    disabled={candidatesAvailableForSelection === 0}
                    title={
                      candidatesAvailableForSelection === 0
                        ? 'No candidates available to select'
                        : 'Select all processable candidates'
                    }
                  >
                    {' '}
                    Select All ({candidatesAvailableForSelection})
                  </button>
                  <button
                    className='coreops-button coreops-button-success'
                    onClick={selectAllQualifiedCandidates}
                    disabled={highlightedAvailableForSelection === 0}
                    title={
                      highlightedAvailableForSelection === 0
                        ? 'No qualified candidates available to select'
                        : 'Select all qualified candidates'
                    }
                  >
                    {' '}
                    <FontAwesomeIcon icon={faUserCheck} /> Select Qualified (
                    {highlightedAvailableForSelection})
                  </button>
                  <button
                    className='coreops-button coreops-button-warning'
                    onClick={clearAllSelections}
                    disabled={selectedCandidates.length === 0}
                  >
                    {' '}
                    Clear Selection
                  </button>
                  {selectedCandidates.length > 0 && (
                    <>
                      <button
                        className='coreops-button coreops-button-info'
                        onClick={() => {
                          const selectedData = processedResults.filter(
                            (result) =>
                              selectedCandidates.includes(result.email)
                          );
                          generateBulkMailLink(selectedData);
                        }}
                        title='Send Google Form link to selected candidates'
                      >
                        {' '}
                        <FontAwesomeIcon icon={faPaperPlane} /> Send Form
                      </button>
                      <button
                        className={`coreops-button coreops-button-primary save-button ${
                          isSaving ? 'loading' : ''
                        }`}
                        onClick={saveSelectedCandidates}
                        disabled={isSaving}
                        title='Save selected candidates to database'
                      >
                        {isSaving ? (
                          <>
                            <ClipLoader
                              color='var(--coreops-light-text)'
                              size={18}
                            />{' '}
                            Saving...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faSave} />
                            {saveIntent === 'qualified' &&
                            selectedCandidates.length > 0 &&
                            selectedCandidates.every((email) =>
                              highlightedResults.find(
                                (hr) =>
                                  hr.email === email &&
                                  !hr.error &&
                                  !hr.analysisError
                              )
                            )
                              ? ' Save Qualified'
                              : ' Save Selected'}
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
                {saveSuccess && successMessage && (
                  <div className='coreops-save-notification success'>
                    <FontAwesomeIcon icon={faCheckCircle} /> {successMessage}
                  </div>
                )}
                {saveError && (
                  <div className='coreops-save-notification error'>
                    <FontAwesomeIcon icon={faExclamationTriangle} /> {saveError}
                  </div>
                )}
              </div>
            )}
          </div>

          {displayResults.length > 0 ? (
            <div className='coreops-table-responsive-wrapper'>
              <table className='coreops-results-table'>
                <thead>
                  <tr>
                    <th style={{ width: '3%' }}>
                      <input
                        type='checkbox'
                        className='coreops-checkbox'
                        title={
                          candidatesAvailableForSelection === 0
                            ? 'No candidates to select'
                            : selectedCandidates.length ===
                              candidatesAvailableForSelection
                            ? 'Deselect all'
                            : 'Select all'
                        }
                        checked={
                          candidatesAvailableForSelection > 0 &&
                          selectedCandidates.length ===
                            candidatesAvailableForSelection
                        }
                        onChange={
                          selectedCandidates.length ===
                          candidatesAvailableForSelection
                            ? clearAllSelections
                            : selectAllCandidates
                        }
                        disabled={candidatesAvailableForSelection === 0}
                      />
                    </th>
                    <th style={{ width: '15%' }}>
                      <FontAwesomeIcon
                        icon={faUserTie}
                        className='coreops-header-icon'
                      />{' '}
                      Name
                    </th>
                    <th style={{ width: '20%' }}>
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className='coreops-header-icon'
                      />{' '}
                      Email
                    </th>
                    <th style={{ width: '7%' }}>
                      <FontAwesomeIcon
                        icon={faStar}
                        className='coreops-header-icon'
                      />{' '}
                      Score
                    </th>
                    <th style={{ width: '35%' }}>
                      <FontAwesomeIcon
                        icon={faFileAlt}
                        className='coreops-header-icon'
                      />{' '}
                      Summary
                    </th>
                    <th style={{ width: '20%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayResults.map((candidate, index) => (
                    <tr
                      key={
                        candidate.email + '-' + index + '-' + candidate.fileName
                      }
                      className={`${
                        candidate.similarityScore >= scoreThreshold &&
                        !candidate.error &&
                        !candidate.analysisError
                          ? 'highlighted-row'
                          : ''
                      } ${
                        candidate.error || candidate.analysisError
                          ? 'error-row'
                          : ''
                      }`}
                    >
                      <td>
                        {!(candidate.error || candidate.analysisError) ? (
                          <input
                            type='checkbox'
                            className='coreops-checkbox'
                            checked={selectedCandidates.includes(
                              candidate.email
                            )}
                            onChange={() =>
                              toggleCandidateSelection(candidate.email)
                            }
                            title={`Select ${candidate.name}`}
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faExclamationCircle}
                            title={
                              candidate.error ||
                              candidate.analysisError ||
                              'Cannot process this candidate'
                            }
                            className='coreops-error-indicator-icon'
                          />
                        )}
                      </td>
                      <td className='coreops-td-name' title={candidate.name}>
                        {candidate.name}
                      </td>
                      <td className='coreops-td-email' title={candidate.email}>
                        {candidate.email}
                      </td>
                      <td
                        className='coreops-td-score'
                        title={`Similarity Score: ${candidate.similarityScore}`}
                      >
                        {candidate.similarityScore}
                      </td>
                      <td
                        className='coreops-td-summary'
                        title={candidate.resumeSummary}
                      >
                        {candidate.error ? (
                          <span className='coreops-error-text'>
                            Error: {candidate.error}
                          </span>
                        ) : candidate.analysisError ? (
                          <span className='coreops-error-text'>
                            Analysis Error: {candidate.analysisError}
                          </span>
                        ) : (
                          candidate.resumeSummary
                        )}
                      </td>
                      <td className='coreops-td-actions'>
                        {!(candidate.error || candidate.analysisError) ? (
                          <>
                            <button
                              className='coreops-button-inline send-form'
                              onClick={() =>
                                generateMailLink(candidate.email, candidate)
                              }
                              title={`Send form to ${candidate.name}`}
                            >
                              <FontAwesomeIcon icon={faPaperPlane} /> Form
                            </button>
                            <button
                              className='coreops-button-inline send-event'
                              onClick={() => generateCalendarInvite(candidate)}
                              title={`Send calendar invite to ${candidate.name}`}
                            >
                              <FontAwesomeIcon icon={faCalendarAlt} /> Event
                            </button>
                          </>
                        ) : (
                          <span className='coreops-action-na'>N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='coreops-message-container no-filter-results'>
              <FontAwesomeIcon icon={faFileAlt} />
              <p>No candidates match the current filter criteria.</p>
              {showFilterOptions && (
                <button
                  className='coreops-button coreops-button-secondary'
                  onClick={() => {
                    setScoreThreshold(55);
                    setShowFilterOptions(false);
                  }}
                >
                  Reset All Filters
                </button>
              )}
            </div>
          )}
        </div>
        <footer className='coreops-footer results-sub-footer'>
          <p>
            © {new Date().getFullYear()} COREHire - Candidate Analysis Dashboard
          </p>
        </footer>
      </div>
    </div>
  );
}

export default ResultsTable;
